import { useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerId, PublicPlayer } from '@protocol';
import { STARTING_LIVES } from '@protocol';


export function Hearts({ lives, dimmed }: { lives: number; dimmed?: boolean }) {
  return (
    <div className="hearts" aria-label={`${lives} hearts remaining`}>
      {Array.from({ length: STARTING_LIVES }, (_, i) => {
        const alive = i < lives;
        return (
          <span
            key={i}
            aria-hidden="true"
            className={`heart ${alive ? 'heart--alive' : 'heart--gone'} ${
              dimmed ? 'heart--dim' : ''
            } ${alive && lives === 1 ? 'heart--last' : ''}`}
          >
            {alive ? '♥' : '♡'}
          </span>
        );
      })}
    </div>
  );
}


export function VideoTile({
  stream,
  player,
  mirrored,
  muted,
  label,
  danger,
  placeholder,
  self: isSelf,
  outcome,
  action,
}: {
  stream: MediaStream | null;
  player?: PublicPlayer | undefined;
  mirrored?: boolean;
  muted?: boolean;
  label: string;
  danger?: boolean;
  placeholder?: React.ReactNode;
  self?: boolean;
  action?: React.ReactNode;
  outcome?: 'winner' | 'loser' | undefined;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (stream) {
      el.play().catch(() => {});
    }
  }, [stream]);

  return (
    <div
      className={`tile ${isSelf ? 'tile--self' : 'tile--them'} ${
        danger ? 'tile--danger' : ''
      } ${outcome ? `tile--${outcome}` : ''}`}
    >
      <video
        ref={ref}
        className={`tile__video ${mirrored ? 'tile__video--mirrored' : ''}`}
        playsInline
        autoPlay
        muted={muted ?? false}
      />
      {!stream && <div className="tile__placeholder">{placeholder ?? label}</div>}
      <div className="tile__overlay">
        {player && <Hearts lives={player.lives} dimmed={!player.connected} />}
        {action}
      </div>
      {player && !player.connected && <div className="tile__badge">Reconnecting&hellip;</div>}
      {outcome === 'winner' && <Confetti />}
    </div>
  );
}

const CONFETTI_COUNT = 36;
const CONFETTI_EMOJI = ['🎉', '🎊', '⭐', '🏆', '✨', '🥳'];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => {
        const angle = (i * 137.508) % 360;
        const distance = 32 + ((i * 37) % 46);
        return {
          key: i,
          emoji: CONFETTI_EMOJI[i % CONFETTI_EMOJI.length]!,
          style: {
            '--angle': `${angle}deg`,
            '--distance': `${distance}%`,
            '--spin': `${((i * 73) % 720) - 360}deg`,
            '--delay': `${(i % 12) * 0.06}s`,
            '--size': `${0.8 + ((i * 13) % 9) / 10}rem`,
          } as React.CSSProperties,
        };
      }),
    [],
  );

  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((piece) => (
        <span key={piece.key} className="confetti__bit" style={piece.style}>
          {piece.emoji}
        </span>
      ))}
    </div>
  );
}


export function WordCards({
  words,
  burned,
}: {
  words: string[];
  burned: string[];
}) {
  if (words.length === 0) return null;
  return (
    <div className="words">
      <p className="words__lead">Get them to say:</p>
      <div className="words__row">
        {words.map((word) => {
          const spent = burned.includes(word);
          return (
            <strong
              key={word}
              className={`wordcard__word ${spent ? 'wordcard__word--burned' : 'wordcard__word--pulse'}`}
            >
              {word}
            </strong>
          );
        })}
      </div>
      {}
      <p className="words__warn">&hellip;but don&rsquo;t say them yourself</p>
    </div>
  );
}


export function Countdown({ startsAt, delayUntil }: { startsAt: number; delayUntil: (t: number) => number }) {
  const [remaining, setRemaining] = useState(() => delayUntil(startsAt));

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setRemaining(delayUntil(startsAt));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [startsAt, delayUntil]);

  const seconds = Math.ceil(remaining / 1000);
  if (seconds <= 0) return <div className="countdown countdown--go">GO</div>;
  return (
    <div className="countdown" key={seconds}>
      {seconds}
    </div>
  );
}


export interface HeartBreakProps {
  word: string;
  isYou: boolean;
  onDone(): void;
  onNotMe(): void;
  delayMs: number;
}

export function HeartBreak({ word, isYou, onDone, onNotMe, delayMs }: HeartBreakProps) {
  const [stage, setStage] = useState<'waiting' | 'freeze' | 'slam' | 'done'>('waiting');

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setStage('freeze'), delayMs));
    timers.push(setTimeout(() => setStage('slam'), delayMs + (isYou ? 260 : 60)));
    timers.push(
      setTimeout(
        () => {
          setStage('done');
          onDone();
        },
        delayMs + 3000,
      ),
    );
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [delayMs, isYou, onDone]);

  if (stage === 'waiting' || stage === 'done') return null;

  return (
    <div className={`fx fx--${stage} ${isYou ? 'fx--yours' : 'fx--theirs'}`} role="alert">
      <div className="fx__vignette" />
      {stage === 'slam' && (
        <>
          {
}
          {
}
          <div className="fx__word">{word}</div>
          <div className="fx__verdict">{isYou ? 'you said it' : 'they said it'}</div>
          <div className="fx__shards" aria-hidden="true">
            {Array.from({ length: 8 }, (_, i) => (
              <span key={i} className={`fx__shard fx__shard--${i}`}>
                ♥
              </span>
            ))}
          </div>
          {isYou && (
            <button type="button" className="fx__notme" onClick={onNotMe}>
              That wasn&rsquo;t me
            </button>
          )}
        </>
      )}
    </div>
  );
}


export function ConnectionBanner({
  socket,
  connection,
  bothPresent,
  onRetry,
}: {
  socket: string;
  connection: string;
  bothPresent: boolean;
  onRetry(): void;
}) {
  if (socket === 'reconnecting') {
    return <div className="banner banner--warn">Reconnecting to the room&hellip;</div>;
  }
  if (!bothPresent) return null;
  if (connection === 'failed') {
    return (
      <div className="banner banner--error">
        The video connection dropped and could not recover.
        <button type="button" className="banner__action" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  if (connection === 'reconnecting') {
    return <div className="banner banner--warn">Video connection is unstable&hellip;</div>;
  }
  if (connection === 'connecting' || connection === 'new') {
    return <div className="banner">Connecting video&hellip;</div>;
  }
  return null;
}

export type { PlayerId };
